import { useEffect, useRef, useState } from "react";

/**
 * C5 — honest tick feedback: returns "up" / "down" for ~{@code durationMs}
 * after the watched number CHANGES, then null. The consumer maps it to a
 * brief background tint via {@code data-flash} (CSS keyframes live in
 * app.css, gated behind {@code prefers-reduced-motion: no-preference}).
 *
 * It never interpolates values — a number-roll would display prices that
 * never existed, which violates the honesty contract. Null/unknown values
 * never flash (unknown is not a movement).
 */
export function useFlashOnChange(
  value: number | null | undefined,
  durationMs = 180,
): "up" | "down" | null {
  const prevRef = useRef<number | null | undefined>(value);
  const [flash, setFlash] = useState<"up" | "down" | null>(null);

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = value;
    if (value == null || prev == null || value === prev) return;
    setFlash(value > prev ? "up" : "down");
    const id = setTimeout(() => setFlash(null), durationMs);
    return () => clearTimeout(id);
  }, [value, durationMs]);

  return flash;
}
